import React from 'react'
import styled from '@emotion/styled'
import { useDispatch, useSelector } from 'react-redux'
import Button from '@mui/material/Button'
import { useMutation } from '@apollo/react-hooks'
import {
  RECALCULATE_ATTACK_BY_GUID,
  SAVE_ATTACK,
  RECALCULATE_ATTACK_LS,
} from '../../queries/attack'
import {
  deleteReduxAttackByGuid,
  saveAttackData,
  updateAttackData,
} from '../../actions'
import { checkNullorEmptyObj } from '../../../../lib/util'
import * as turf from '@turf/turf'

const Container = styled.div`
  bottom: 20px;
  right: 20px;
  z-index: 1001;
  position: absolute;
  padding: 20px;
  background: white;
  box-shadow: 0 0 10px 2px rgba(0, 0, 0, 0.2);
  border-radius: 4px;
`

export default function ReCalculate(props) {
  const {
    selectedAttack,
    remoteStorage,
    obstacles,
    selectedProjectId,
    projects,
    selectedScenarioId,
  } = useSelector(state => state)
  const [recalculate] = useMutation(RECALCULATE_ATTACK_BY_GUID)
  const [recalculateLS] = useMutation(RECALCULATE_ATTACK_LS)
  const [saveAttack] = useMutation(SAVE_ATTACK)
  const dispatch = useDispatch()
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  if (!checkNullorEmptyObj(selectedAttack)) {
    // don't recalculate on a recalculated attack (original_id indicates it is one)
    if (selectedAttack.original_id) {
      return null
    }
  }

  const combinedObstacles = () => {
    let polygon, centroid, attackGeojson, distance
    attackGeojson = JSON.parse(selectedAttack.geojson).geometry.coordinates
    let combinedObstacles = obstacles.map(obstacle => {
      return JSON.parse(obstacle.geojson).geometry.coordinates[0]
    })

    let projectBuildings = []
    let project = projects.filter(p => p.id === selectedProjectId)

    if (project[0].geojson) {
      projectBuildings = JSON.parse(project[0].geojson)
        .features.filter(building => {
          return building.geometry.type === 'Polygon'
        })
        .map(building => {
          try {
            // filter out any obstacle beyond the path length
            polygon = turf.polygon(building.geometry.coordinates)
            centroid = turf.centroid(polygon)

            distance = turf.distance(
              turf.getCoords(centroid),
              turf.point(attackGeojson[0]),
              {
                units: 'meters',
              }
            )
            if (distance > 2 * selectedAttack.path_length) return null
          } catch (e) {
            console.log('combinedObstacles ', e)
            return null
          }

          return building.geometry.coordinates[0]
        })
        .filter(item => item)
    }

    return combinedObstacles.concat(projectBuildings)
  }

  const clickActionsForRemoteDB = async () => {
    try {
      let obj

      // delete the old recalculated attack from state and the map, if it exists
      if (selectedAttack.recalculated_id) {
        props.draw.delete([selectedAttack.recalculated_id])
        dispatch(deleteReduxAttackByGuid(selectedAttack.recalculated_id))
      }

      // create the recalculated attack in the DB
      const recalculated = await recalculate({
        variables: { id: selectedAttack.geom_guid },
      })

      // update the recalculated attack with the guid info
      let geojson = JSON.parse(
        recalculated.data.recalculateAttackByGuid.geojson
      )
      const featureIds = props.draw.add(geojson)
      geojson.id = featureIds[0]
      obj = {
        id: recalculated.data.recalculateAttackByGuid.id,
        geojson: JSON.stringify(geojson),
        geom_guid: featureIds[0],
      }
      await saveAttack({ variables: { input: obj } })

      // update the old attack in DB with a ref to the recalculated
      obj = {
        id: selectedAttack.id,
        recalculated_id: featureIds[0],
      }
      await saveAttack({ variables: { input: obj } })

      // update the old attack in state with a ref to the recalculated
      obj = { ...selectedAttack }
      obj['recalculated_id'] = featureIds[0]
      dispatch(updateAttackData(selectedAttack.id, obj))

      // put the recalculated attack into state
      obj = {
        id: recalculated.data.recalculateAttackByGuid.id,
        name: recalculated.data.recalculateAttackByGuid.name,
        geojson: JSON.stringify(geojson),
        start_speed: recalculated.data.recalculateAttackByGuid.start_speed,
        end_speed: recalculated.data.recalculateAttackByGuid.end_speed,
        path_length: recalculated.data.recalculateAttackByGuid.path_length,
        vehicle: recalculated.data.recalculateAttackByGuid.vehicle,
        geom_guid: featureIds[0],
        original_id: selectedAttack.id,
        project_id: selectedProjectId,
        scenario_id: selectedScenarioId,
        line_color: '#ff0000',
      }
      dispatch(saveAttackData(obj))
    } catch (err) {
      console.error('Recalculate (remote) failed', {
        message: err?.message,
        graphQLErrors: err?.graphQLErrors,
        networkError: err?.networkError,
      })
      throw err
    }
  }

  const clickActionsForLocalStorage = async () => {
    try {
      let obj

      // delete the old recalculated attack from state and the map, if it exists
      if (selectedAttack.recalculated_id) {
        props.draw.delete([selectedAttack.recalculated_id])
        dispatch(deleteReduxAttackByGuid(selectedAttack.recalculated_id))
      }

      const recalculateObstacles = combinedObstacles()

      const attackInput = {
        id: selectedAttack.id,
        name: selectedAttack.name,
        geojson: selectedAttack.geojson,
        start_speed: selectedAttack.start_speed,
        end_speed: selectedAttack.end_speed,
        path_length: selectedAttack.path_length,
        vehicle: selectedAttack.vehicle,
        units: selectedAttack.units,
      }

      const recalculated = await recalculateLS({
        variables: {
          input: {
            obstacles: recalculateObstacles,
            attack: attackInput,
          },
        },
      })

      // update the recalculated attack with the guid info
      let geojson = JSON.parse(recalculated.data.recalculateAttackLS.geojson)
      geojson.properties = {
        ...(geojson.properties || {}),
        line_color: '#ff0000',
      }
      const featureIds = props.draw.add(geojson)
      geojson.id = featureIds[0]

      // update the old attack in state with a ref to the recalculated
      obj = { ...selectedAttack }
      obj['recalculated_id'] = featureIds[0]
      dispatch(updateAttackData(selectedAttack.id, obj))

      // put the recalculated attack into state
      obj = {
        id: recalculated.data.recalculateAttackLS.id,
        name: recalculated.data.recalculateAttackLS.name,
        geojson: JSON.stringify(geojson),
        start_speed: recalculated.data.recalculateAttackLS.start_speed,
        end_speed: recalculated.data.recalculateAttackLS.end_speed,
        path_length: recalculated.data.recalculateAttackLS.path_length,
        vehicle: recalculated.data.recalculateAttackLS.vehicle,
        geom_guid: featureIds[0],
        original_id: selectedAttack.id,
        project_id: selectedProjectId,
        scenario_id: selectedScenarioId,
        units: { ...selectedAttack.units },
        all_vehicle_data:
          recalculated.data.recalculateAttackLS.all_vehicle_data,
        line_color: '#ff0000',
      }
      dispatch(saveAttackData(obj))
    } catch (err) {
      console.error('Recalculate (local storage mode) failed', {
        message: err?.message,
        graphQLErrors: err?.graphQLErrors,
        networkError: err?.networkError,
      })
      throw err
    }
  }

  const click = async () => {
    if (isSubmitting) return
    setIsSubmitting(true)
    try {
      if (!remoteStorage.attacks) {
        await clickActionsForLocalStorage()
      } else {
        await clickActionsForRemoteDB()
      }
    } catch (err) {
      // already logged above
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      {!checkNullorEmptyObj(selectedAttack) ? (
        <Container>
          <Button onClick={click} color="primary" disabled={isSubmitting}>
            {isSubmitting ? 'ReCalculating...' : 'ReCalculate'}
          </Button>
        </Container>
      ) : null}
    </>
  )
}
